/**
 * 🎁 PackRipReveal — shared pack-opening cinematic (Feb 2026)
 *
 * Flow:
 *   1. Pack art appears centered with a glowing "Tap to Rip" prompt.
 *   2. First tap triggers a rip animation (shake → tear → split).
 *   3. Cards fan out in an arc, face-down, each tappable to flip.
 *   4. Rarity-based glow bursts on each flip.
 *   5. "Reveal All" shortcut flips every remaining card.
 *   6. Dupe-refund banner + close once everything is face-up.
 *
 * Used by:
 *   • DragoniousCCG — all pack-store purchases.
 *   • SpinWheel — 3-Card Draw slot payout.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { X, Volume2, VolumeX, Sparkles } from "lucide-react";
import { GOLDEN_APEX_URL, DRAGONIOUS_MUTE_KEY } from "@/lib/dragoniousAudio";

const RARITY_FRAME = {
  common:    { ring: "ring-neutral-500/60",   glow: "0 0 24px rgba(163,163,163,0.45)",   chip: "bg-neutral-700 text-white",       stars: "★",      label: "Common"    },
  uncommon:  { ring: "ring-neutral-300/70",   glow: "0 0 28px rgba(229,229,229,0.55)",   chip: "bg-neutral-500 text-white",       stars: "★★",     label: "Uncommon"  },
  rare:      { ring: "ring-blue-400/70",      glow: "0 0 36px rgba(96,165,250,0.75)",    chip: "bg-blue-700 text-white",          stars: "★★★",    label: "Rare"      },
  epic:      { ring: "ring-fuchsia-400/80",   glow: "0 0 48px rgba(232,121,249,0.9)",    chip: "bg-fuchsia-700 text-white",       stars: "★★★★",   label: "Epic"      },
  legendary: { ring: "ring-amber-400/90",     glow: "0 0 60px rgba(251,191,36,1)",       chip: "bg-amber-600 text-white",         stars: "★★★★★",  label: "Legendary" },
  mythic:    { ring: "ring-red-500/90",       glow: "0 0 72px rgba(239,68,68,1)",        chip: "bg-red-700 text-white",           stars: "★★★★★★", label: "Mythic"    },
  cosmic:    { ring: "ring-cyan-300/90",      glow: "0 0 80px rgba(103,232,249,1)",      chip: "bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white", stars: "✦✦✦✦✦✦✦", label: "Cosmic" },
};

const RARITY_ORDER = ["cosmic", "mythic", "legendary", "epic", "rare", "uncommon", "common"];
function highestRarity(cards) {
  for (const r of RARITY_ORDER) {
    if ((cards || []).some((c) => c.rarity === r)) return r;
  }
  return "common";
}

// ── Single face-down → face-up card tile ────────────────────────
function FlipCard({ card, index, revealed, onFlip, fanAngle }) {
  const frame = RARITY_FRAME[card.rarity] || RARITY_FRAME.common;
  const glowStyle = revealed ? { boxShadow: frame.glow } : {};
  // Fan-out — small arc so cards don't crash into each other on
  // mobile. Reset to flat when the modal switches to grid mode.
  const transform = revealed
    ? "rotate(0deg) translateY(0)"
    : `rotate(${fanAngle}deg) translateY(-6px)`;

  return (
    <button
      type="button"
      onClick={() => !revealed && onFlip(index)}
      data-testid={`pack-flip-card-${index}`}
      className="relative group focus:outline-none"
      style={{
        perspective: "1000px",
        transform,
        transformOrigin: "bottom center",
        transition: "transform 400ms cubic-bezier(0.22, 1, 0.36, 1)",
      }}
      aria-label={revealed ? `${card.name} — ${frame.label}` : "Tap to flip card"}
    >
      <div
        className="relative w-[112px] h-[158px] sm:w-[128px] sm:h-[180px] rounded-xl"
        style={{
          transformStyle: "preserve-3d",
          transform: revealed ? "rotateY(0deg)" : "rotateY(180deg)",
          transition: "transform 600ms cubic-bezier(0.22, 1, 0.36, 1)",
          ...glowStyle,
        }}
      >
        {/* FACE (front) */}
        <div
          className={`absolute inset-0 rounded-xl ring-2 ${frame.ring} bg-gradient-to-br from-neutral-950 to-black p-2 flex flex-col justify-between`}
          style={{ backfaceVisibility: "hidden" }}
        >
          <div className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded self-start ${frame.chip}`}>
            {frame.stars}
          </div>
          <div className="flex-1 flex items-center justify-center text-5xl">
            {card.icon || "🎴"}
          </div>
          <div className="text-center">
            <div className="text-[11px] font-bold text-white leading-tight line-clamp-2">
              {card.name || "???"}
            </div>
            <div className="text-[9px] text-neutral-400 uppercase tracking-widest mt-0.5">
              {frame.label}
            </div>
          </div>
          {card.duplicate && (
            <div className="absolute top-2 right-2 text-[8px] uppercase tracking-widest font-bold bg-yellow-500 text-black px-1 py-0.5 rounded shadow">
              Dupe
            </div>
          )}
        </div>

        {/* BACK (card back) */}
        <div
          className="absolute inset-0 rounded-xl ring-2 ring-fuchsia-500/50 bg-gradient-to-br from-purple-900 via-fuchsia-900 to-black flex items-center justify-center overflow-hidden"
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            boxShadow: "0 0 16px rgba(217,70,239,0.4)",
          }}
        >
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: "radial-gradient(circle at 50% 50%, rgba(217,70,239,0.6) 0%, transparent 60%)",
            }}
          />
          <div className="relative text-center">
            <div className="text-4xl mb-1">🐦‍🔥</div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-fuchsia-200 font-bold">
              Dragonious
            </div>
          </div>
          <div className="absolute inset-0 ring-1 ring-fuchsia-400/30 rounded-xl pointer-events-none group-hover:ring-fuchsia-300/80 transition-all" />
        </div>
      </div>

      {!revealed && (
        <div className="absolute -bottom-5 left-0 right-0 text-center">
          <span className="text-[9px] uppercase tracking-widest text-fuchsia-300/80 font-bold animate-pulse">
            Tap to Flip
          </span>
        </div>
      )}
    </button>
  );
}

// ── Pack art (face-down pack before the rip) ────────────────────
function PackArt({ icon, name, ripped, onRip }) {
  return (
    <button
      type="button"
      onClick={onRip}
      disabled={ripped}
      data-testid="pack-rip-trigger"
      className="relative mx-auto block focus:outline-none"
      style={{ width: 220, height: 300 }}
      aria-label="Tap to rip the pack open"
    >
      {/* Left half */}
      <div
        className="absolute inset-y-0 left-0 w-1/2 rounded-l-2xl border-2 border-fuchsia-500/60 bg-gradient-to-br from-purple-900 via-fuchsia-900 to-black flex items-center justify-end overflow-hidden"
        style={{
          transform: ripped ? "translateX(-120%) rotate(-14deg)" : "translateX(0) rotate(0)",
          transition: "transform 650ms cubic-bezier(0.22, 1, 0.36, 1)",
          boxShadow: ripped ? "none" : "0 0 60px rgba(217,70,239,0.5)",
          clipPath: ripped
            ? "polygon(0 0, 100% 0, 92% 15%, 100% 30%, 88% 55%, 100% 80%, 95% 100%, 0 100%)"
            : "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
        }}
      >
        <div className="text-7xl pr-2 opacity-90 translate-x-6">{icon}</div>
      </div>
      {/* Right half */}
      <div
        className="absolute inset-y-0 right-0 w-1/2 rounded-r-2xl border-2 border-fuchsia-500/60 bg-gradient-to-bl from-fuchsia-900 via-purple-900 to-black flex items-center justify-start overflow-hidden"
        style={{
          transform: ripped ? "translateX(120%) rotate(14deg)" : "translateX(0) rotate(0)",
          transition: "transform 650ms cubic-bezier(0.22, 1, 0.36, 1)",
          boxShadow: ripped ? "none" : "0 0 60px rgba(168,85,247,0.5)",
          clipPath: ripped
            ? "polygon(8% 0, 100% 0, 100% 100%, 5% 100%, 12% 80%, 0 55%, 12% 30%, 0 15%)"
            : "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
        }}
      >
        <div className="text-7xl pl-2 opacity-90 -translate-x-6">{icon}</div>
      </div>

      {/* Center seam highlight */}
      {!ripped && (
        <>
          <div
            className="absolute inset-y-6 left-1/2 w-[2px] bg-fuchsia-300/60"
            style={{ transform: "translateX(-50%)", filter: "blur(1px)" }}
          />
          <div className="absolute inset-x-0 bottom-6 text-center">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/70 border border-fuchsia-400/60">
              <Sparkles size={12} className="text-fuchsia-300 animate-pulse" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-200 font-bold">
                Tap to Rip
              </span>
            </div>
          </div>
          <div className="absolute inset-x-0 top-3 text-center">
            <div className="text-[9px] uppercase tracking-[0.3em] text-fuchsia-300/80 font-bold">
              {name}
            </div>
          </div>
        </>
      )}

      {/* Burst behind the pack */}
      {ripped && (
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(217,70,239,0.5) 0%, transparent 60%)",
            animation: "packBurst 650ms ease-out forwards",
          }}
        />
      )}
    </button>
  );
}

// ── Main modal ─────────────────────────────────────────────────
export default function PackRipReveal({ result, onClose, title }) {
  const [phase, setPhase] = useState("sealed"); // sealed → ripped → done
  const [flipped, setFlipped] = useState([]); // array of booleans
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(DRAGONIOUS_MUTE_KEY) === "1"; } catch { return false; }
  });

  const cards = result?.cards || [];
  const totalCards = cards.length;
  const packIcon = result?.pack?.icon || "🎁";
  const packName = result?.pack?.name || title || "Pack Opened";

  // Reset per-pack state whenever a new result arrives.
  useEffect(() => {
    if (!result) return;
    setPhase("sealed");
    setFlipped(new Array(totalCards).fill(false));
  }, [result, totalCards]);

  // Golden Apex soundtrack lifecycle.
  useEffect(() => {
    if (!result) return;
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = 0;
    el.volume = 0.55;
    el.muted = muted;
    const p = el.play();
    if (p && typeof p.catch === "function") p.catch(() => { /* autoplay blocked — silent fallback */ });
    return () => {
      try { el.pause(); el.currentTime = 0; } catch { /* noop */ }
    };
  }, [result]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
    try { localStorage.setItem(DRAGONIOUS_MUTE_KEY, muted ? "1" : "0"); } catch { /* noop */ }
  }, [muted]);

  const revealedCount = useMemo(() => flipped.filter(Boolean).length, [flipped]);

  // Auto-advance to "done" view when every card is flipped.
  useEffect(() => {
    if (phase === "ripped" && totalCards > 0 && revealedCount === totalCards) {
      const id = setTimeout(() => setPhase("done"), 650);
      return () => clearTimeout(id);
    }
  }, [phase, revealedCount, totalCards]);

  if (!result) return null;

  const rip = () => {
    if (phase !== "sealed") return;
    setTimeout(() => setPhase("ripped"), 500);
  };

  const flipAt = (i) => {
    setFlipped((prev) => {
      if (prev[i]) return prev;
      const next = [...prev];
      next[i] = true;
      return next;
    });
  };

  const revealAll = () => setFlipped(new Array(totalCards).fill(true));

  // Fan layout — centered small arc.
  const fanAngleFor = (i) => {
    if (totalCards <= 1) return 0;
    const spread = Math.min(28, totalCards * 4);
    const step = spread / Math.max(1, totalCards - 1);
    return -spread / 2 + i * step;
  };

  const dupeRefund = Number(result.dupe_refund_unbound_od || 0);
  const bestRarity = highestRarity(cards);
  const bestFrame = RARITY_FRAME[bestRarity] || RARITY_FRAME.common;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={phase === "done" ? onClose : undefined}
      data-testid="pack-rip-reveal"
    >
      {/* Keyframes for the burst behind the pack */}
      <style>{`
        @keyframes packBurst {
          0%   { transform: scale(0.3); opacity: 0.9; }
          100% { transform: scale(2.2); opacity: 0; }
        }
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-4xl w-full max-h-[94vh] overflow-y-auto rounded-2xl border-2 bg-gradient-to-br from-purple-950/95 via-black/95 to-fuchsia-950/95 p-6"
        style={{
          borderColor: "rgba(217,70,239,0.5)",
          boxShadow: `0 0 80px rgba(217, 70, 239, 0.4), ${phase === "done" ? bestFrame.glow : "none"}`,
        }}
      >
        <audio ref={audioRef} src={GOLDEN_APEX_URL} loop preload="auto" data-testid="pack-rip-audio" />

        <button
          onClick={() => setMuted((m) => !m)}
          data-testid="pack-rip-mute"
          className="absolute top-3 right-12 text-neutral-400 hover:text-fuchsia-300 p-1"
          title={muted ? "Unmute" : "Mute"}
        >
          {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
        <button
          onClick={onClose}
          data-testid="pack-rip-close"
          className="absolute top-3 right-3 text-neutral-400 hover:text-white p-1"
        >
          <X size={22} />
        </button>

        <div className="text-center mb-5">
          <div className="text-[10px] uppercase tracking-[0.35em] text-fuchsia-300 font-bold">
            {phase === "sealed" ? "Pack Sealed" : phase === "ripped" ? "Flip Your Cards" : "Pack Opened"}
          </div>
          <div className="text-2xl font-bold text-white">{packName}</div>
          {phase === "ripped" && (
            <div className="text-xs text-neutral-400 mt-1" data-testid="pack-rip-progress">
              {revealedCount} / {totalCards} revealed
            </div>
          )}
        </div>

        {phase === "sealed" && (
          <div className="py-6" data-testid="pack-rip-sealed">
            <PackArt icon={packIcon} name={packName} ripped={false} onRip={rip} />
          </div>
        )}

        {phase !== "sealed" && (
          <div className="relative py-2" data-testid="pack-rip-cards">
            <div className="flex flex-wrap justify-center items-end gap-3 sm:gap-4 pb-6">
              {cards.map((c, i) => (
                <FlipCard
                  key={i}
                  card={c}
                  index={i}
                  revealed={flipped[i]}
                  onFlip={flipAt}
                  fanAngle={phase === "ripped" ? fanAngleFor(i) : 0}
                />
              ))}
            </div>

            {phase === "ripped" && revealedCount < totalCards && (
              <div className="text-center mt-2">
                <button
                  onClick={revealAll}
                  data-testid="pack-rip-reveal-all"
                  className="text-[10px] uppercase tracking-widest px-4 py-1.5 rounded border border-fuchsia-500/50 text-fuchsia-200 hover:bg-fuchsia-500/10 hover:text-white transition"
                >
                  Reveal All
                </button>
              </div>
            )}
          </div>
        )}

        {phase === "done" && dupeRefund > 0 && (
          <div
            className="rounded-lg border border-yellow-500/40 bg-yellow-900/20 p-3 text-center mb-3 mt-3"
            data-testid="pack-rip-dupe-refund"
          >
            <div className="text-[10px] uppercase tracking-widest text-yellow-300">Duplicate Refund</div>
            <div className="text-lg font-bold text-yellow-200">💎 +{dupeRefund} Unbound OmniDollars</div>
          </div>
        )}

        {phase === "done" && (
          <div className="text-center mt-2">
            <button
              onClick={onClose}
              data-testid="pack-rip-done"
              className="px-6 py-2 rounded-lg bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white font-bold uppercase tracking-widest text-xs hover:brightness-125 transition"
            >
              Collect
            </button>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-3">
              {result.packs_opened_total != null && `Packs opened total · ${result.packs_opened_total}`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
